import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, FormControl, MenuItem, Select, TextField, Typography, useTheme } from '@mui/material'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import { Icons } from '../../../utils/utilities'
import * as Yup from 'yup'
import React, { useCallback, useEffect, useState } from 'react'
import { useUserStore } from '../../../store/user.store'
import { useQuizAndQuestionbankStore } from '../../../store/quizandquestionbank.store'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import { useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react'

const AddQuestionBank = observer( ( { state, setState, subject = null } ) => {

    const UserStore = useUserStore()
    const QuizStore = useQuizAndQuestionbankStore()

    const [loading, setLoading] = useState( false )
    const [adding, setAdding] = useState( false )

    const navigate = useNavigate()

    const { palette, border } = useTheme()

    const initialValues = {
        name: '',
        subject: subject || '-',
    }

    const validationSchema = Yup.object().shape( {
        name: Yup.string().required( "Question bank name is required!" ).max( 240, "Max length 240 characters" ),
        subject: Yup.mixed().required( 'Subject is required' ).notOneOf( ["-"], "Please select a question bank subject" )
    } )

    const addQuestionBank = async ( values, { resetForm } ) => {
        setAdding( true )
        const success = await QuizStore.addQuestionBank( values )

        if ( success ) {
            resetForm()
            setState( false )
            navigate( `${success.data}` )
        }
        setAdding( false )
    }


    const getFacultySubjects = useCallback( async () => {
        setLoading( true )
        await UserStore.fetchUserSubjects( Boolean( UserStore.getUserSubjectsDetails.cached ) )
        setLoading( false )
    }, [UserStore] )

    useEffect( () => {
        if ( UserStore.getErrorsStatuses?.subjects === true )
            getFacultySubjects()
    }, [UserStore, getFacultySubjects] )

    return (
        <Dialog open={state} slotProps={{
            paper: { sx: { width: "90%", maxWidth: "650px", background: palette.form.formCardBg } }
        }}>
            <DialogTitle sx={{ padding: "20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                Create Question Bank
            </DialogTitle>
            <DialogContent>
                <Box>
                    <Formik onSubmit={addQuestionBank} initialValues={initialValues} validationSchema={validationSchema}>
                        {( { values, setFieldValue, resetForm } ) => (
                            <Form>
                                <Box borderRadius="5px" border={border[1]} bgcolor={palette.form.formBg} padding="20px" >
                                    <FormControl fullWidth>
                                        <label htmlFor="QB-name">
                                            <Typography display="block" variant="subtitle2" gutterBottom color={palette.form.labelColor}>Question bank name *</Typography>
                                        </label>
                                        <Field autoComplete="off" id="QB-name" size="small" sx={{ fontSize: "14px" }} InputProps={{ sx: { fontSize: "14px" } }} placeholder="Eg: Data Structures question bank" as={TextField} name="name" type="text" />
                                        <Typography fontSize="12px" color="errorMessage.main"> <ErrorMessage name='name' /> </Typography>
                                    </FormControl>
                                    <FormControl margin="normal" fullWidth>
                                        <label htmlFor="QB-subject">
                                            <Typography display="block" variant="subtitle2" gutterBottom color={palette.form.labelColor}>Subject * {subject !== null && "(Pre-filled)"} </Typography>
                                        </label>
                                        <Field
                                            // id="QB-subject"
                                            size="small"
                                            as={Select}
                                            onChange={( e ) => {
                                                setFieldValue( 'subject', UserStore.getUserSubjectsDetails?.currentSubjects?.find( s => s.subject_code === e.target.value ) )
                                            }}
                                            disabled={loading || Boolean( subject )}
                                            placeholder="Select the question bank related subject"
                                            value={values?.subject?.subject_code || "-"}
                                            sx={{ textTransform: "capitalize", fontSize: "14px", color: values.subject === "-" && '#cbcbcb' }}
                                            name='subject'
                                        >
                                            <MenuItem disabled value="-" dense sx={{ color: "GrayText", fontSize: "14px", paddingY: "5px" }}>{loading ? <DataLoadingSpinner waitingMessage="Loading subjects..." padding='0' size={14} /> : "Eg: Data structures "}</MenuItem>
                                            {UserStore.getUserSubjectsDetails?.currentSubjects?.map( ( sub, index ) => (
                                                <MenuItem value={sub.subject_code} key={sub.subject_code} dense sx={{ fontSize: "14px", paddingY: "5px", textTransform: "capitalize" }}>
                                                    {`${sub.subject_name?.toLowerCase()} (`}<strong>{`${sub.subject_code})`}</strong>
                                                </MenuItem>
                                            ) )}
                                        </Field>
                                        {!subject && UserStore.getErrorsStatuses?.subjects && <Box bgcolor={palette.errorMessage.light + "22"} marginTop="10px" borderRadius="5px" padding="5px 10px" border={`1px dotted ${palette.errorMessage.main}`} display="flex" alignItems="center" gap="5px" fontSize="12px" justifyContent="space-between" color="textSecondary">
                                            <Typography color="errorMessage.main" variant='subtitle2'>Failed to fetch <strong><em>subjects</em></strong> from ERP...</Typography>
                                            <Button color="errorMessage" sx={{ textTransform: "capitalize" }} onClick={() => getFacultySubjects()} endIcon={loading ? <CircularProgress size={14} /> : Icons.default.Replay}>Retry</Button>
                                        </Box>}
                                        {!subject && loading && !UserStore.getErrorsStatuses?.subjects && <Typography marginTop="5px" display="flex" alignItems="center" gap="5px" fontSize="12px" color="textSecondary"> <DataLoadingSpinner padding='0' size={14} /> {`Fetching your subjects from ERP...`} </Typography>}
                                        <Typography marginTop="5px" fontSize="12px" color="errorMessage.main"> <ErrorMessage name='subject' /> </Typography>
                                    </FormControl>

                                    <Box display="flex" marginTop="30px" gap="20px" flexWrap="wrap" alignItems="center">
                                        <Button sx={{ textTransform: "capitalize" }} variant="contained" disabled={adding} disableElevation type="submit" startIcon={adding ? <CircularProgress size={14} /> : Icons.default.AddIcon}>{adding ? "Adding new question bank..." : "Add Question bank"}</Button>
                                        <Button disabled={adding} sx={{ textTransform: "capitalize" }} onClick={() => { setState( false ); resetForm() }} variant="contained" disableElevation color="error" >Cancel</Button>
                                    </Box>
                                </Box>

                            </Form>
                        )}
                    </Formik>
                </Box>
            </DialogContent>
        </Dialog >
    )
} )

export default AddQuestionBank
